import { Controller } from '@hotwired/stimulus'
import { FetchRequest } from '@rails/request.js'

export default class extends Controller {
  static targets = ['submit']

  initialize () {
    this.autoSave = this.throttle(this.autoSave, 5000)
    this.debouncedSubmitFormToTurboFrame = this.debounce(this.submitFormToTurboFrame, 200).bind(this)
  }

  beforeMorphElement (event) {
    const currentElement = event.target
    const incomingElement = event.detail.newElement

    if (this.isActiveElement(currentElement)) {
      event.preventDefault()
      return
    }

    if (this.isJsOwned(currentElement)) {
      event.preventDefault() // stop Turbo from morphing this one node
      currentElement.replaceWith(incomingElement) // attach the new element → fresh init
    }
  }

  isActiveElement (element) {
    return document.activeElement === element
  }

  update (event) {
    if (this.disabledFor(event)) return

    if (this.shouldMorphInstantly(event)) {
      this.submitFormToTurboFrame(event)
    } else if (this.shouldMorphDebounced(event)) {
      this.debouncedSubmitFormToTurboFrame(event)
    } else {
      this.autoSave()
    }
  }

  ensureSubmitTarget () {
    if (!this.hasSubmitTarget) {
      this.insertSubmitTarget()
    }
  }

  submitFormToTurboFrame (event) {
    this.ensureSubmitTarget()
    this.element.requestSubmit(this.submitTarget)
  }

  async autoSave () {
    const data = new FormData(this.element)
    data.set('dynamic_form', true)
    const url = this.element.action
    const httpMethod = data.get('_method') || this.element.method
    const request = new FetchRequest(httpMethod, url, { body: data })
    await request.perform()
  }

  shouldMorphInstantly (event) {
    const isRadio = event.target instanceof window.HTMLInputElement && event.target.type === 'radio'
    const isSelect = event.target instanceof window.HTMLSelectElement
    const isCheckbox = event.target instanceof window.HTMLInputElement && event.target.type === 'checkbox'
    const isHidden = event.target instanceof window.HTMLInputElement && event.target.type === 'hidden'
    // Synthetic events (dispatchEvent) come from widgets that already
    // express deliberate intent — number-steppers, searchable pickers,
    // etc. Skip the typing-style debounce for those.
    const isSynthetic = event.isTrusted === false

    return isRadio || isSelect || isCheckbox || isHidden || isSynthetic
  }

  shouldMorphDebounced (event) {
    const isTextInput = event.target instanceof window.HTMLInputElement && event.target.type === 'text'
    const isNumberInput = event.target instanceof window.HTMLInputElement && event.target.type === 'number'
    const isTextArea = event.target instanceof window.HTMLTextAreaElement

    return isTextInput || isNumberInput || isTextArea
  }

  insertSubmitTarget () {
    const submitter = document.createElement('input')
    submitter.classList.add('hidden')
    submitter.type = 'submit'
    submitter.name = 'dynamic_form'
    submitter.value = 'true'
    submitter.dataset.dynamicFormTarget = 'submit'
    this.element.appendChild(submitter)
  }

  disabledFor (event) {
    return event.target.dataset.disableDynamic === 'true'
  }

  isJsOwned (el) {
    // Prevent morphing the controller's root element, since this will have a data-controller attribute
    if (el === this.element) return false

    // Stimulus controller
    if (el.hasAttribute('data-controller')) {
      return true
    }

    // Custom element (must contain a dash and be registered)
    if (el.localName.includes('-') && window.customElements.get(el.localName)) {
      return true
    }

    return false
  }

  // Utility functions for throttling and debouncing
  throttle (func, limit) {
    let inThrottle
    return function () {
      const args = arguments
      const context = this
      if (!inThrottle) {
        func.apply(context, args)
        inThrottle = true
        setTimeout(() => {
          inThrottle = false
        }, limit)
      }
    }
  }

  debounce (func, delay) {
    let timeoutId
    return function (...args) {
      clearTimeout(timeoutId)
      timeoutId = setTimeout(() => func.apply(this, args), delay)
    }
  }
}
